import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { api } from '@/utils/api';
import { applyBrandColor } from '@/lib/theme';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const res = await api.getMe();
      setUser(res.data);
      // Paint the interface in the organisation's colour. Best effort: if the
      // request fails the default stays, which is better than a blank wait.
      api.getSettings()
        .then((s) => applyBrandColor(s.data?.primary_color))
        .catch(() => {});
    } catch {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      // Try cookie-based auth (Google OAuth)
      api.getMe().then(res => {
        setUser(res.data);
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const res = await api.login({ email, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return res;
  };

  // The backend has already completed the Google exchange and handed us a
  // ready-to-use session token in the redirect fragment.
  const loginWithGoogleToken = async (sessionToken) => {
    localStorage.setItem('token', sessionToken);
    setToken(sessionToken);
    const res = await api.getMe();
    setUser(res.data);
    return res;
  };

  const logout = async () => {
    try { await api.googleLogout(); } catch (e) { console.debug(e); }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, loginWithGoogleToken, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
